import { useState } from 'react';
import { createPortal } from 'react-dom';
import { T } from '../reportTokens';
import type { ExportFormat } from '../../../utils/reportExport';

export type ExportScope = 'outlet' | 'all';

interface FormatOption {
  value: ExportFormat;
  label: string;
  icon: string;
}

const FORMAT_OPTIONS: FormatOption[] = [
  { value: 'csv', label: 'CSV', icon: 'table_chart' },
  { value: 'excel', label: 'Excel', icon: 'grid_on' },
  { value: 'pdf', label: 'PDF', icon: 'picture_as_pdf' },
];

/**
 * Scope (This outlet / All outlets) + Format (CSV / Excel / PDF) export
 * dialog for multi-outlet accounts. One unified responsive component — no
 * mobile/desktop fork, per the reports architecture rules.
 */
export function ExportScopeModal({
  title,
  open,
  onClose,
  currentOutletName,
  outletCount,
  onConfirm,
  loading = false,
  progressLabel,
  multiOutletDisabled = false,
}: {
  title: string;
  open: boolean;
  onClose: () => void;
  currentOutletName: string;
  outletCount: number;
  onConfirm: (scope: ExportScope, format: ExportFormat) => void;
  loading?: boolean;
  progressLabel?: string;
  multiOutletDisabled?: boolean;
}) {
  const [scope, setScope] = useState<ExportScope>('outlet');
  const [format, setFormat] = useState<ExportFormat>('csv');

  if (!open) return null;

  const scopeCard = (value: ExportScope, label: string, desc: string) => {
    const active = scope === value;
    const disabled = value === 'all' && multiOutletDisabled;
    return (
      <div
        key={value}
        onClick={() => { if (!disabled) setScope(value); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
          borderRadius: 10, border: `1.5px solid ${active ? T.accent : T.cardBorder}`,
          background: active ? T.accentSoft : T.surface,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.45 : 1,
          marginBottom: 10,
        }}
      >
        <span style={{
          width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
          border: `2px solid ${active ? T.accent : T.cardBorder}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {active && <span style={{ width: 9, height: 9, borderRadius: '50%', background: T.accent }} />}
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.t1 }}>{label}</div>
          <div style={{ fontSize: 11, color: T.t3, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{desc}</div>
        </div>
      </div>
    );
  };

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(20,22,32,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={() => { if (!loading) onClose(); }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto',
          background: T.surface, borderRadius: 16, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          fontFamily: T.font,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: T.t1 }}>{title}</div>
            <div style={{ fontSize: 12, color: T.t3, marginTop: 3 }}>Choose scope and format</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
            style={{ background: 'none', border: 'none', cursor: loading ? 'default' : 'pointer', color: T.t3, padding: 4 }}
          >
            <span className="material-symbols-rounded" style={{ fontSize: 20 }}>close</span>
          </button>
        </div>

        <div style={{ fontSize: 11, fontWeight: 700, color: T.t3, letterSpacing: 0.5, marginBottom: 8 }}>SCOPE</div>
        {scopeCard('outlet', 'This outlet', currentOutletName)}
        {scopeCard('all', 'All outlets', `${outletCount} outlets you can access`)}

        <div style={{ fontSize: 11, fontWeight: 700, color: T.t3, letterSpacing: 0.5, margin: '14px 0 8px' }}>FORMAT</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
          {FORMAT_OPTIONS.map((opt) => {
            const active = format === opt.value;
            return (
              <div
                key={opt.value}
                onClick={() => setFormat(opt.value)}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                  padding: '16px 8px', borderRadius: 10, cursor: 'pointer',
                  border: `1.5px solid ${active ? T.accent : T.cardBorder}`,
                  background: active ? T.accentSoft : T.surface,
                }}
              >
                <span className="material-symbols-rounded" style={{ fontSize: 22, color: active ? T.accent : T.t2 }}>{opt.icon}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: active ? T.accent : T.t1 }}>{opt.label}</span>
              </div>
            );
          })}
        </div>

        {loading && progressLabel && (
          <div style={{ fontSize: 12, color: T.t2, marginBottom: 12, textAlign: 'center' }}>{progressLabel}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            style={{
              padding: '9px 18px', borderRadius: 8, border: `1px solid ${T.cardBorder}`,
              background: T.surface, color: T.t1, fontSize: 13, fontWeight: 600,
              cursor: loading ? 'default' : 'pointer', fontFamily: T.font,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(scope, format)}
            disabled={loading}
            style={{
              padding: '9px 20px', borderRadius: 8, border: 'none',
              background: T.accent, color: '#fff', fontSize: 13, fontWeight: 700,
              cursor: loading ? 'default' : 'pointer', fontFamily: T.font,
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? 'Exporting…' : 'Export'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
